import React, { useState, useEffect } from 'react';
import ToolLayout from '../components/ToolLayout';

interface ConversionResult {
  unix: number;
  unixMs: number;
  iso: string;
  utc: string;
  local: string;
  ago: string;
}

const TimestampConverter: React.FC = () => {
  const [input, setInput] = useState('');
  const [inputType, setInputType] = useState<'unix' | 'iso' | 'readable'>('unix');
  const [result, setResult] = useState<ConversionResult | null>(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [currentTime, setCurrentTime] = useState<ConversionResult | null>(null);
  const [timezone, setTimezone] = useState('UTC');
  const [batchInput, setBatchInput] = useState('');
  const [batchResults, setBatchResults] = useState<Array<{ input: string; result: ConversionResult | null; error?: string }>>([]);

  // Update current time every second
  useEffect(() => {
    const updateCurrentTime = () => {
      const now = Date.now();
      setCurrentTime(generateConversionResult(now));
    };

    updateCurrentTime();
    const interval = setInterval(updateCurrentTime, 1000);

    return () => clearInterval(interval);
  }, []);

  const timezones = [
    'UTC',
    'America/New_York',
    'America/Chicago',
    'America/Denver',
    'America/Los_Angeles',
    'Europe/London',
    'Europe/Paris',
    'Europe/Berlin',
    'Asia/Tokyo',
    'Asia/Shanghai',
    'Asia/Hong_Kong',
    'Asia/Singapore',
    'Asia/Dubai',
    'Australia/Sydney',
    'Pacific/Auckland'
  ];

  const getRelativeTime = (timestamp: number): string => {
    const now = Date.now();
    const diff = now - timestamp;
    const seconds = Math.floor(Math.abs(diff) / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    const months = Math.floor(days / 30);
    const years = Math.floor(days / 365);

    const future = diff < 0;
    const suffix = future ? 'from now' : 'ago';

    if (seconds < 60) {
      return `${seconds} second${seconds !== 1 ? 's' : ''} ${suffix}`;
    }
    if (minutes < 60) {
      return `${minutes} minute${minutes !== 1 ? 's' : ''} ${suffix}`;
    }
    if (hours < 24) {
      return `${hours} hour${hours !== 1 ? 's' : ''} ${suffix}`;
    }
    if (days < 30) {
      return `${days} day${days !== 1 ? 's' : ''} ${suffix}`;
    }
    if (months < 12) {
      return `${months} month${months !== 1 ? 's' : ''} ${suffix}`;
    }
    return `${years} year${years !== 1 ? 's' : ''} ${suffix}`;
  };

  const formatWithTimezone = (date: Date, tz: string): string => {
    try {
      return date.toLocaleString('en-US', {
        timeZone: tz,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      });
    } catch (err) {
      return date.toLocaleString();
    }
  };

  const generateConversionResult = (timestamp: number): ConversionResult => {
    const date = new Date(timestamp);
    return {
      unix: Math.floor(timestamp / 1000),
      unixMs: timestamp,
      iso: date.toISOString(),
      utc: date.toUTCString(),
      local: formatWithTimezone(date, timezone),
      ago: getRelativeTime(timestamp)
    };
  };

  const convertTimestamp = () => {
    setError('');
    setSuccess('');
    setResult(null);

    if (!input.trim()) {
      setError('Please enter a timestamp or date');
      return;
    }

    try {
      let timestamp: number;

      if (inputType === 'unix') {
        // Unix timestamp (seconds or milliseconds)
        const num = parseFloat(input.trim());
        if (isNaN(num)) {
          throw new Error('Invalid Unix timestamp');
        }
        // Auto-detect if it's seconds or milliseconds
        timestamp = num < 10000000000 ? num * 1000 : num;
      } else if (inputType === 'iso') {
        // ISO date string
        const date = new Date(input.trim());
        if (isNaN(date.getTime())) {
          throw new Error('Invalid ISO date format');
        }
        timestamp = date.getTime();
      } else {
        // Readable date
        const date = new Date(input.trim());
        if (isNaN(date.getTime())) {
          throw new Error('Invalid date format');
        }
        timestamp = date.getTime();
      }

      const conversionResult = generateConversionResult(timestamp);
      setResult(conversionResult);
      setSuccess('Conversion successful! 🕒');
    } catch (err) {
      setError('Conversion failed: ' + (err as Error).message);
      setResult(null);
    }
  };

  const convertBatch = () => {
    setError('');
    setSuccess('');
    setBatchResults([]);

    if (!batchInput.trim()) {
      setError('Please enter timestamps (one per line)');
      return;
    }

    const lines = batchInput.trim().split('\n').filter(line => line.trim());
    const results = lines.map(line => {
      try {
        const num = parseFloat(line.trim());
        if (isNaN(num)) {
          return { input: line, result: null, error: 'Invalid number' };
        }
        const timestamp = num < 10000000000 ? num * 1000 : num;
        return { input: line, result: generateConversionResult(timestamp), error: undefined };
      } catch (err) {
        return { input: line, result: null, error: (err as Error).message };
      }
    });

    setBatchResults(results);
    setSuccess(`Converted ${results.filter(r => r.result).length}/${results.length} timestamps! 📊`);
  };

  const useCurrentTime = () => {
    if (currentTime) {
      setInput(currentTime.unix.toString());
      setInputType('unix');
      convertTimestamp();
    }
  };

  const clearAll = () => {
    setInput('');
    setResult(null);
    setError('');
    setSuccess('');
    setBatchInput('');
    setBatchResults([]);
  };

  const copyToClipboard = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setSuccess(`Copied ${label} to clipboard!`);
    } catch (err) {
      setError('Failed to copy to clipboard');
    }
  };

  return (
    <ToolLayout
      title="Timestamp Converter"
      description="Convert between Unix timestamps, ISO dates, and readable formats with timezone support"
    >
      {/* Current Time Display */}
      {currentTime && (
        <div className="current-time-section">
          <h3 className="current-time-title">⏰ Current Time</h3>
          <div className="current-time-grid">
            <div className="time-item">
              <span className="time-label">Unix (seconds):</span>
              <code className="time-value">{currentTime.unix}</code>
            </div>
            <div className="time-item">
              <span className="time-label">Unix (ms):</span>
              <code className="time-value">{currentTime.unixMs}</code>
            </div>
            <div className="time-item">
              <span className="time-label">ISO 8601:</span>
              <code className="time-value">{currentTime.iso}</code>
            </div>
            <div className="time-item">
              <span className="time-label">UTC:</span>
              <code className="time-value">{currentTime.utc}</code>
            </div>
          </div>
          <button className="action-button secondary" onClick={useCurrentTime} style={{ marginTop: '1rem' }}>
            Use Current Time
          </button>
        </div>
      )}

      {/* Input Type Selection */}
      <div className="input-group">
        <label className="input-label">Input Type:</label>
        <div className="input-type-selection">
          <label className="input-type-option">
            <input
              type="radio"
              value="unix"
              checked={inputType === 'unix'}
              onChange={(e) => setInputType(e.target.value as 'unix')}
            />
            <span>Unix Timestamp</span>
          </label>
          <label className="input-type-option">
            <input
              type="radio"
              value="iso"
              checked={inputType === 'iso'}
              onChange={(e) => setInputType(e.target.value as 'iso')}
            />
            <span>ISO 8601</span>
          </label>
          <label className="input-type-option">
            <input
              type="radio"
              value="readable"
              checked={inputType === 'readable'}
              onChange={(e) => setInputType(e.target.value as 'readable')}
            />
            <span>Readable Date</span>
          </label>
        </div>
      </div>

      {/* Timezone Selection */}
      <div className="input-group">
        <label className="input-label">Timezone for Local Display:</label>
        <select
          value={timezone}
          onChange={(e) => setTimezone(e.target.value)}
          className="timezone-select"
        >
          {timezones.map(tz => (
            <option key={tz} value={tz}>{tz}</option>
          ))}
        </select>
      </div>

      {/* Single Conversion */}
      <div className="input-group">
        <label className="input-label">Enter Timestamp or Date:</label>
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={
            inputType === 'unix' ? '1234567890 or 1234567890000' :
            inputType === 'iso' ? '2024-01-01T12:00:00Z' :
            'January 1, 2024 12:00:00'
          }
          className="timestamp-input"
          onKeyPress={(e) => e.key === 'Enter' && convertTimestamp()}
        />
      </div>

      <div className="button-group">
        <button className="action-button" onClick={convertTimestamp}>
          🔄 Convert
        </button>
        <button className="action-button secondary" onClick={clearAll}>
          Clear All
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      {/* Conversion Result */}
      {result && (
        <div className="conversion-result">
          <h3 className="result-title">📅 Conversion Result:</h3>
          <div className="result-grid">
            <div className="result-item">
              <div className="result-header">
                <span className="result-label">Unix Timestamp (seconds):</span>
                <button
                  className="copy-btn"
                  onClick={() => copyToClipboard(result.unix.toString(), 'Unix timestamp')}
                >
                  📋
                </button>
              </div>
              <code className="result-value">{result.unix}</code>
            </div>

            <div className="result-item">
              <div className="result-header">
                <span className="result-label">Unix Timestamp (milliseconds):</span>
                <button
                  className="copy-btn"
                  onClick={() => copyToClipboard(result.unixMs.toString(), 'Unix timestamp (ms)')}
                >
                  📋
                </button>
              </div>
              <code className="result-value">{result.unixMs}</code>
            </div>

            <div className="result-item">
              <div className="result-header">
                <span className="result-label">ISO 8601:</span>
                <button
                  className="copy-btn"
                  onClick={() => copyToClipboard(result.iso, 'ISO date')}
                >
                  📋
                </button>
              </div>
              <code className="result-value">{result.iso}</code>
            </div>

            <div className="result-item">
              <div className="result-header">
                <span className="result-label">UTC:</span>
                <button
                  className="copy-btn"
                  onClick={() => copyToClipboard(result.utc, 'UTC date')}
                >
                  📋
                </button>
              </div>
              <code className="result-value">{result.utc}</code>
            </div>

            <div className="result-item">
              <div className="result-header">
                <span className="result-label">Local ({timezone}):</span>
                <button
                  className="copy-btn"
                  onClick={() => copyToClipboard(result.local, 'Local date')}
                >
                  📋
                </button>
              </div>
              <code className="result-value">{result.local}</code>
            </div>

            <div className="result-item">
              <div className="result-header">
                <span className="result-label">Relative Time:</span>
                <button
                  className="copy-btn"
                  onClick={() => copyToClipboard(result.ago, 'Relative time')}
                >
                  📋
                </button>
              </div>
              <code className="result-value">{result.ago}</code>
            </div>
          </div>
        </div>
      )}

      {/* Batch Conversion */}
      <div className="batch-section">
        <h3 className="batch-title">📊 Batch Conversion</h3>
        <div className="input-group">
          <label className="input-label">Enter Unix Timestamps (one per line):</label>
          <textarea
            value={batchInput}
            onChange={(e) => setBatchInput(e.target.value)}
            placeholder={'1234567890\n1609459200\n1672531200'}
            className="input-textarea"
            rows={6}
            style={{ fontFamily: 'Monaco, Menlo, Ubuntu Mono, monospace' }}
          />
        </div>
        <button className="action-button" onClick={convertBatch}>
          🔄 Convert Batch
        </button>

        {batchResults.length > 0 && (
          <div className="batch-results">
            <h4 className="batch-results-title">Batch Results ({batchResults.length}):</h4>
            <div className="batch-results-grid">
              {batchResults.map((item, index) => (
                <div key={index} className={`batch-result-item ${item.error ? 'error' : ''}`}>
                  <div className="batch-result-header">
                    <strong>Input:</strong> <code>{item.input}</code>
                  </div>
                  {item.result ? (
                    <div className="batch-result-content">
                      <div><strong>ISO:</strong> {item.result.iso}</div>
                      <div><strong>Ago:</strong> {item.result.ago}</div>
                    </div>
                  ) : (
                    <div className="batch-error">{item.error}</div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Info Section */}
      <div className="timestamp-info-section">
        <h4 className="timestamp-info-title">📖 Timestamp Guide:</h4>
        <div className="timestamp-info-content">
          <p>
            <strong>Unix Timestamp:</strong> Number of seconds (or milliseconds) since January 1, 1970 00:00:00 UTC
          </p>
          <p>
            <strong>ISO 8601:</strong> International standard date format (e.g., 2024-01-01T12:00:00Z)
          </p>
          <p>
            <strong>Readable Date:</strong> Human-readable format like "January 1, 2024 12:00:00"
          </p>

          <strong>Common Examples:</strong>
          <ul className="timestamp-info-list">
            <li>Unix timestamp: <code>1234567890</code> (seconds) or <code>1234567890000</code> (milliseconds)</li>
            <li>ISO 8601: <code>2024-01-01T12:00:00Z</code> or <code>2024-01-01T12:00:00+00:00</code></li>
            <li>Readable: <code>January 1, 2024 12:00:00</code> or <code>2024/01/01 12:00:00</code></li>
          </ul>
        </div>
      </div>

      <style>{`
        .current-time-section {
          padding: 1.5rem;
          background: var(--accent-light);
          border: 2px solid var(--accent-primary);
          border-radius: 12px;
          margin-bottom: 2rem;
        }

        .current-time-title {
          margin: 0 0 1rem 0;
          color: var(--accent-primary);
          font-size: 1.25rem;
          font-weight: 600;
        }

        .current-time-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 0.75rem;
        }

        .time-item {
          padding: 0.75rem;
          background: var(--card-bg);
          border-radius: 6px;
          border: 1px solid var(--border-color);
        }

        .time-label {
          display: block;
          font-size: 0.85rem;
          color: var(--text-secondary);
          margin-bottom: 0.25rem;
          font-weight: 600;
        }

        .time-value {
          display: block;
          font-family: Monaco, Menlo, Ubuntu Mono, monospace;
          font-size: 0.9rem;
          color: var(--text-primary);
        }

        .input-type-selection {
          display: flex;
          gap: 1.5rem;
          flex-wrap: wrap;
        }

        .input-type-option {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          cursor: pointer;
          color: var(--text-primary);
        }

        .timezone-select,
        .timestamp-input {
          width: 100%;
          padding: 0.75rem;
          border: 2px solid var(--border-color);
          border-radius: 8px;
          background: var(--bg-primary);
          color: var(--text-primary);
          font-size: 0.9375rem;
          transition: all 0.3s ease;
        }

        .timestamp-input {
          font-family: Monaco, Menlo, Ubuntu Mono, monospace;
        }

        .timezone-select:focus,
        .timestamp-input:focus {
          outline: none;
          border-color: var(--accent-primary);
          box-shadow: 0 0 0 3px var(--accent-light);
        }

        .timestamp-input::placeholder {
          color: var(--text-tertiary);
        }

        .conversion-result {
          margin-top: 2rem;
          padding: 1.5rem;
          background: var(--bg-secondary);
          border: 2px solid var(--border-color);
          border-radius: 12px;
        }

        .result-title {
          margin: 0 0 1rem 0;
          color: var(--text-primary);
          font-size: 1.25rem;
          font-weight: 600;
        }

        .result-grid {
          display: grid;
          gap: 1rem;
        }

        .result-item {
          padding: 1rem;
          background: var(--card-bg);
          border: 2px solid var(--border-color);
          border-radius: 8px;
        }

        .result-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 0.5rem;
        }

        .result-label {
          font-weight: 600;
          color: var(--text-secondary);
          font-size: 0.9rem;
        }

        .copy-btn {
          background: transparent;
          border: none;
          cursor: pointer;
          font-size: 1rem;
          padding: 0.25rem 0.5rem;
          opacity: 0.7;
          transition: opacity 0.2s;
        }

        .copy-btn:hover {
          opacity: 1;
        }

        .result-value {
          display: block;
          font-family: Monaco, Menlo, Ubuntu Mono, monospace;
          font-size: 0.95rem;
          color: var(--accent-primary);
          word-break: break-all;
        }

        .batch-section {
          margin-top: 2rem;
          padding: 1.5rem;
          background: var(--bg-secondary);
          border: 2px solid var(--border-color);
          border-radius: 12px;
        }

        .batch-title {
          margin: 0 0 1rem 0;
          color: var(--text-primary);
          font-size: 1.25rem;
          font-weight: 600;
        }

        .batch-results {
          margin-top: 1.5rem;
        }

        .batch-results-title {
          margin: 0 0 1rem 0;
          color: var(--text-primary);
          font-weight: 600;
        }

        .batch-results-grid {
          display: grid;
          gap: 0.75rem;
        }

        .batch-result-item {
          padding: 0.75rem;
          background: var(--card-bg);
          border: 2px solid var(--border-color);
          border-radius: 6px;
        }

        .batch-result-item.error {
          border-color: #dc2626;
        }

        .batch-result-header {
          margin-bottom: 0.5rem;
          color: var(--text-primary);
        }

        .batch-result-header code {
          background: var(--bg-primary);
          padding: 0.2rem 0.4rem;
          border-radius: 3px;
          font-size: 0.85rem;
        }

        .batch-result-content {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
          font-size: 0.9rem;
          color: var(--text-secondary);
        }

        .batch-error {
          color: #dc2626;
          font-size: 0.9rem;
        }

        .timestamp-info-section {
          margin-top: 2rem;
          padding: 1.5rem;
          background: var(--bg-secondary);
          border-radius: 12px;
          border: 2px solid var(--accent-primary);
        }

        .timestamp-info-title {
          margin: 0 0 1rem 0;
          color: var(--accent-primary);
          font-weight: 600;
        }

        .timestamp-info-content {
          color: var(--text-secondary);
          font-size: 0.9rem;
          line-height: 1.6;
        }

        .timestamp-info-content p {
          margin: 0 0 0.5rem 0;
        }

        .timestamp-info-list {
          margin: 0.5rem 0;
          padding-left: 1.5rem;
        }

        .timestamp-info-list li {
          margin-bottom: 0.25rem;
        }

        .timestamp-info-content code {
          background: var(--card-bg);
          padding: 0.2rem 0.4rem;
          border-radius: 3px;
          font-family: Monaco, Menlo, Ubuntu Mono, monospace;
          color: var(--text-primary);
        }

        @media (max-width: 768px) {
          .current-time-grid {
            grid-template-columns: 1fr;
          }

          .input-type-selection {
            flex-direction: column;
            gap: 0.75rem;
          }
        }
      `}</style>
    </ToolLayout>
  );
};

export default TimestampConverter;